import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import './Workflow.css';

const Workflow = () => {
  const { user } = useAuth();
  const [abaAtiva, setAbaAtiva] = useState('meus-workflows');
  const [workflows, setWorkflows] = useState([]);
  const [todosWorkflows, setTodosWorkflows] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [selectedWorkflow, setSelectedWorkflow] = useState(null);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [showModalWorkflow, setShowModalWorkflow] = useState(false);
  const [showModalTemplate, setShowModalTemplate] = useState(false);
  const [showModalCriarTemplate, setShowModalCriarTemplate] = useState(false);
  const [filtroStatus, setFiltroStatus] = useState('todos');
  const [busca, setBusca] = useState('');
  
  // Estados para ações
  const [acao, setAcao] = useState('');
  const [observacoes, setObservacoes] = useState('');
  const [justificativa, setJustificativa] = useState('');
  
  // Estados para criar template
  const [novoTemplate, setNovoTemplate] = useState({
    nome: '',
    descricao: '',
    tipo_documento: '',
    id_secretaria: '',
    ativo: true,
    etapas: []
  });
  const [etapaAtual, setEtapaAtual] = useState({
    nome_etapa: '',
    descricao: '',
    tipo_etapa: 'aprovacao',
    ordem: 1,
    id_usuario_responsavel: '',
    id_setor_responsavel: '',
    permite_rejeicao: true,
    prazo_dias: null
  });

  const isAdmin = user?.tipo_usuario === 'admin';
  const isGestor = user?.nivel_permissao >= 2 || user?.tipo_usuario === 'gestor';
  const podeGerenciar = isAdmin || isGestor;

  useEffect(() => {
    carregarDados();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [abaAtiva, filtroStatus]);

  const carregarDados = async () => {
    setLoading(true);
    setError('');
    
    try {
      if (abaAtiva === 'meus-workflows') {
        await carregarWorkflows();
      } else if (abaAtiva === 'todos-workflows') {
        await carregarTodosWorkflows();
      } else if (abaAtiva === 'templates') {
        await carregarTemplates();
      }
    } catch (err) {
      setError('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  const carregarWorkflows = async () => {
    try {
      const status = filtroStatus === 'todos' ? undefined : filtroStatus;
      const response = await api.get(`/workflow/meus-workflows${status ? `?status=${status}` : ''}`);
      if (response.data.success) {
        setWorkflows(response.data.data || []);
      }
    } catch (err) {
      setError('Erro ao carregar workflows');
    }
  };

  const carregarTodosWorkflows = async () => {
    try {
      if (!podeGerenciar) {
        setError('Você não tem permissão para ver todos os workflows');
        return;
      }
      const status = filtroStatus === 'todos' ? undefined : filtroStatus;
      const response = await api.get(`/workflow/meus-workflows${status ? `?status=${status}` : ''}`);
      if (response.data.success) {
        setTodosWorkflows(response.data.data || []);
      }
    } catch (err) {
      setError('Erro ao carregar todos os workflows');
    }
  };

  const carregarTemplates = async () => {
    try {
      const response = await api.get('/workflow/templates');
      if (response.data.success) {
        setTemplates(response.data.data || []);
      }
    } catch (err) {
      setError('Erro ao carregar templates');
    }
  };

  const abrirWorkflow = async (idInstancia) => {
    try {
      const response = await api.get(`/workflow/${idInstancia}`);
      if (response.data.success) {
        setSelectedWorkflow(response.data.data);
        setShowModalWorkflow(true);
      }
    } catch (err) {
      setError('Erro ao carregar detalhes do workflow');
    }
  };

  const abrirTemplate = async (template) => {
    setSelectedTemplate(template);
    setShowModalTemplate(true);
  };

  const executarAcao = async () => {
    if (!acao) {
      setError('Selecione uma ação');
      return;
    }

    if (acao === 'rejeitado' && !justificativa.trim()) {
      setError('Justificativa é obrigatória para rejeição');
      return;
    }

    try {
      setError('');
      await api.post(`/workflow/${selectedWorkflow.id_instancia}/executar`, {
        acao,
        observacoes: observacoes.trim() || null,
        justificativa: justificativa.trim() || null
      });

      setSuccess('Ação executada com sucesso!');
      setShowModalWorkflow(false);
      setAcao('');
      setObservacoes('');
      setJustificativa('');
      await carregarDados();
      
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.message || 'Erro ao executar ação');
    }
  };

  const adicionarEtapa = () => {
    if (!etapaAtual.nome_etapa.trim()) {
      setError('Nome da etapa é obrigatório');
      return;
    }

    const novaEtapa = {
      ...etapaAtual,
      ordem: novoTemplate.etapas.length + 1
    };

    setNovoTemplate({
      ...novoTemplate,
      etapas: [...novoTemplate.etapas, novaEtapa]
    });

    setEtapaAtual({
      nome_etapa: '',
      descricao: '',
      tipo_etapa: 'aprovacao',
      ordem: novoTemplate.etapas.length + 2,
      id_usuario_responsavel: '',
      id_setor_responsavel: '',
      permite_rejeicao: true,
      prazo_dias: null
    });
  };

  const removerEtapa = (index) => {
    const novasEtapas = novoTemplate.etapas.filter((_, i) => i !== index);
    setNovoTemplate({
      ...novoTemplate,
      etapas: novasEtapas.map((etapa, i) => ({ ...etapa, ordem: i + 1 }))
    });
  };

  const criarTemplate = async () => {
    if (!novoTemplate.nome.trim()) {
      setError('Nome do template é obrigatório');
      return;
    }

    if (novoTemplate.etapas.length === 0) {
      setError('Adicione pelo menos uma etapa');
      return;
    }

    try {
      setError('');
      const response = await api.post('/workflow/templates', novoTemplate);
      
      if (response.data.success) {
        setSuccess('Template criado com sucesso!');
        setShowModalCriarTemplate(false);
        setNovoTemplate({
          nome: '',
          descricao: '',
          tipo_documento: '',
          id_secretaria: '',
          ativo: true,
          etapas: []
        });
        await carregarTemplates();
        setTimeout(() => setSuccess(''), 3000);
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Erro ao criar template');
    }
  };

  const formatarData = (data) => {
    if (!data) return '-';
    return new Date(data).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const workflowsFiltrados = (lista) => {
    if (!busca.trim()) return lista;
    const buscaLower = busca.toLowerCase();
    return lista.filter(w => 
      w.protocolo?.numero_protocolo?.toLowerCase().includes(buscaLower) ||
      w.protocolo?.assunto?.toLowerCase().includes(buscaLower) ||
      w.template?.nome?.toLowerCase().includes(buscaLower)
    );
  };

  const templatesFiltrados = () => {
    if (!busca.trim()) return templates;
    const buscaLower = busca.toLowerCase();
    return templates.filter(t => 
      t.nome?.toLowerCase().includes(buscaLower) ||
      t.descricao?.toLowerCase().includes(buscaLower)
    );
  };

  return (
    <div className="workflow-page">
      <div className="workflow-header">
        <div className="header-content">
          <div>
            <h1>⚙️ Gerenciar Workflow</h1>
            <p>Gerencie workflows e templates de processos</p>
          </div>
          {podeGerenciar && abaAtiva === 'templates' && (
            <button
              className="btn-criar-template"
              onClick={() => setShowModalCriarTemplate(true)}
            >
              ➕ Criar Template
            </button>
          )}
        </div>
      </div>

      {/* Mensagens */}
      {error && (
        <div className="error-message">
          {error}
          <button onClick={() => setError('')}>×</button>
        </div>
      )}

      {success && (
        <div className="success-message">
          {success}
          <button onClick={() => setSuccess('')}>×</button>
        </div>
      )}

      {/* Abas */}
      <div className="workflow-tabs">
        <button
          className={`tab-button ${abaAtiva === 'meus-workflows' ? 'active' : ''}`}
          onClick={() => setAbaAtiva('meus-workflows')}
        >
          📋 Meus Workflows
        </button>
        {podeGerenciar && (
          <button
            className={`tab-button ${abaAtiva === 'todos-workflows' ? 'active' : ''}`}
            onClick={() => setAbaAtiva('todos-workflows')}
          >
            🌐 Todos os Workflows
          </button>
        )}
        <button
          className={`tab-button ${abaAtiva === 'templates' ? 'active' : ''}`}
          onClick={() => setAbaAtiva('templates')}
        >
          📝 Templates
        </button>
      </div>

      {/* Filtros */}
      <div className="workflow-filters">
        <div className="filter-group">
          <label>Status:</label>
          <select
            value={filtroStatus}
            onChange={(e) => setFiltroStatus(e.target.value)}
            className="filter-select"
          >
            <option value="todos">Todos</option>
            <option value="em_andamento">Em Andamento</option>
            <option value="aprovado">Aprovado</option>
            <option value="rejeitado">Rejeitado</option>
            <option value="finalizado">Finalizado</option>
            <option value="cancelado">Cancelado</option>
          </select>
        </div>
        <div className="filter-group">
          <label>Buscar:</label>
          <input
            type="text"
            value={busca}
            onChange={(e) => setBusca(e.target.value)}
            placeholder="Protocolo, assunto ou template..."
            className="filter-input"
          />
        </div>
      </div>

      {/* Conteúdo das Abas */}
      {loading ? (
        <div className="loading">Carregando...</div>
      ) : (
        <>
          {/* Aba: Meus Workflows */}
          {abaAtiva === 'meus-workflows' && (
            <div className="workflows-list">
              {workflowsFiltrados(workflows).length === 0 ? (
                <div className="empty-state">
                  <p>Nenhum workflow encontrado.</p>
                </div>
              ) : (
                workflowsFiltrados(workflows).map((workflow) => (
                  <div key={workflow.id_instancia} className="workflow-card">
                    <div className="workflow-card-header">
                      <div className="workflow-protocolo">
                        <strong>{workflow.protocolo?.numero_protocolo || 'N/A'}</strong>
                        <span>{workflow.protocolo?.assunto || 'Sem assunto'}</span>
                      </div>
                      <span className={`workflow-status-badge status-${workflow.status}`}>
                        {workflow.status === 'em_andamento' && '🔄 Em Andamento'}
                        {workflow.status === 'aprovado' && '✅ Aprovado'}
                        {workflow.status === 'rejeitado' && '❌ Rejeitado'}
                        {workflow.status === 'finalizado' && '✔️ Finalizado'}
                        {workflow.status === 'cancelado' && '🚫 Cancelado'}
                      </span>
                    </div>
                    <div className="workflow-card-body">
                      {workflow.etapaAtual && (
                        <div className="workflow-etapa-info">
                          <strong>Etapa Atual:</strong> {workflow.etapaAtual.nome_etapa}
                          {workflow.etapaAtual.descricao && (
                            <div className="workflow-etapa-desc">{workflow.etapaAtual.descricao}</div>
                          )}
                        </div>
                      )}
                      {workflow.template && (
                        <div className="workflow-template-info">
                          <strong>Template:</strong> {workflow.template.nome}
                        </div>
                      )}
                      <div className="workflow-meta">
                        <span>Iniciado em: {formatarData(workflow.data_inicio)}</span>
                      </div>
                    </div>
                    <div className="workflow-card-actions">
                      <button
                        className="btn-ver-workflow"
                        onClick={() => abrirWorkflow(workflow.id_instancia)}
                      >
                        Ver Detalhes
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
          )}

          {/* Aba: Todos os Workflows */}
          {abaAtiva === 'todos-workflows' && podeGerenciar && (
            <div className="workflows-list">
              {workflowsFiltrados(todosWorkflows).length === 0 ? (
                <div className="empty-state">
                  <p>Nenhum workflow encontrado.</p>
                </div>
              ) : (
                workflowsFiltrados(todosWorkflows).map((workflow) => (
                  <div key={workflow.id_instancia} className="workflow-card">
                    <div className="workflow-card-header">
                      <div className="workflow-protocolo">
                        <strong>{workflow.protocolo?.numero_protocolo || 'N/A'}</strong>
                        <span>{workflow.protocolo?.assunto || 'Sem assunto'}</span>
                      </div>
                      <span className={`workflow-status-badge status-${workflow.status}`}>
                        {workflow.status === 'em_andamento' && '🔄 Em Andamento'}
                        {workflow.status === 'aprovado' && '✅ Aprovado'}
                        {workflow.status === 'rejeitado' && '❌ Rejeitado'}
                        {workflow.status === 'finalizado' && '✔️ Finalizado'}
                        {workflow.status === 'cancelado' && '🚫 Cancelado'}
                      </span>
                    </div>
                    <div className="workflow-card-body">
                      {workflow.etapaAtual && (
                        <div className="workflow-etapa-info">
                          <strong>Etapa Atual:</strong> {workflow.etapaAtual.nome_etapa}
                        </div>
                      )}
                      <div className="workflow-meta">
                        <span>Iniciado em: {formatarData(workflow.data_inicio)}</span>
                      </div>
                    </div>
                    <div className="workflow-card-actions">
                      <button
                        className="btn-ver-workflow"
                        onClick={() => abrirWorkflow(workflow.id_instancia)}
                      >
                        Ver Detalhes
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
          )}

          {/* Aba: Templates */}
          {abaAtiva === 'templates' && (
            <div className="templates-list">
              {templatesFiltrados().length === 0 ? (
                <div className="empty-state">
                  <p>Nenhum template encontrado.</p>
                </div>
              ) : (
                templatesFiltrados().map((template) => (
                  <div key={template.id_template} className="template-card">
                    <div className="template-card-header">
                      <div>
                        <h3>{template.nome}</h3>
                        {template.descricao && <p className="template-desc">{template.descricao}</p>}
                      </div>
                      <span className={`template-status ${template.ativo ? 'ativo' : 'inativo'}`}>
                        {template.ativo ? '✅ Ativo' : '❌ Inativo'}
                      </span>
                    </div>
                    <div className="template-card-body">
                      <div className="template-info">
                        {template.tipo_documento && (
                          <span className="template-badge">📄 {template.tipo_documento}</span>
                        )}
                        {template.etapas && (
                          <span className="template-badge">📋 {template.etapas.length} etapas</span>
                        )}
                      </div>
                      {template.etapas && template.etapas.length > 0 && (
                        <div className="template-etapas-preview">
                          <strong>Etapas:</strong>
                          <div className="etapas-list">
                            {template.etapas.map((etapa, idx) => (
                              <span key={idx} className="etapa-badge">
                                {idx + 1}. {etapa.nome_etapa}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                    <div className="template-card-actions">
                      <button
                        className="btn-ver-template"
                        onClick={() => abrirTemplate(template)}
                      >
                        Ver Detalhes
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
          )}
        </>
      )}

      {/* Modal: Detalhes do Workflow */}
      {showModalWorkflow && selectedWorkflow && (
        <div className="modal-overlay" onClick={() => setShowModalWorkflow(false)}>
          <div className="modal-content-workflow" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header-workflow">
              <h2>Detalhes do Workflow</h2>
              <button className="modal-close" onClick={() => setShowModalWorkflow(false)}>×</button>
            </div>

            <div className="modal-body-workflow">
              <div className="workflow-detail-section">
                <h3>Informações Gerais</h3>
                <div className="workflow-detail-info">
                  <div className="detail-item">
                    <label>Protocolo:</label>
                    <span>{selectedWorkflow.protocolo?.numero_protocolo || 'N/A'}</span>
                  </div>
                  <div className="detail-item">
                    <label>Assunto:</label>
                    <span>{selectedWorkflow.protocolo?.assunto || 'N/A'}</span>
                  </div>
                  <div className="detail-item">
                    <label>Status:</label>
                    <span className={`workflow-status-badge status-${selectedWorkflow.status}`}>
                      {selectedWorkflow.status}
                    </span>
                  </div>
                  {selectedWorkflow.template && (
                    <div className="detail-item">
                      <label>Template:</label>
                      <span>{selectedWorkflow.template.nome}</span>
                    </div>
                  )}
                  <div className="detail-item">
                    <label>Iniciado em:</label>
                    <span>{formatarData(selectedWorkflow.data_inicio)}</span>
                  </div>
                </div>
              </div>

              {/* Timeline do Workflow */}
              {selectedWorkflow.template && selectedWorkflow.template.etapas && (
                <div className="workflow-detail-section">
                  <h3>Fluxo do Workflow</h3>
                  <div className="workflow-timeline">
                    {selectedWorkflow.template.etapas.map((etapa, idx) => {
                      const executada = selectedWorkflow.execucoes?.some(
                        exec => exec.id_etapa === etapa.id_etapa
                      );
                      const atual = selectedWorkflow.etapaAtual?.id_etapa === etapa.id_etapa;
                      
                      return (
                        <div key={etapa.id_etapa} className={`timeline-item ${executada ? 'concluida' : ''} ${atual ? 'atual' : ''}`}>
                          <div className="timeline-marker">
                            {executada ? '✓' : atual ? '🔄' : '○'}
                          </div>
                          <div className="timeline-content">
                            <div className="timeline-etapa-nome">{etapa.nome_etapa}</div>
                            {etapa.descricao && (
                              <div className="timeline-etapa-desc">{etapa.descricao}</div>
                            )}
                            {atual && (
                              <div className="timeline-etapa-atual">Etapa Atual</div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {selectedWorkflow.etapaAtual && (
                <div className="workflow-detail-section">
                  <h3>Etapa Atual</h3>
                  <div className="workflow-etapa-detail">
                    <div className="etapa-nome">{selectedWorkflow.etapaAtual.nome_etapa}</div>
                    {selectedWorkflow.etapaAtual.descricao && (
                      <div className="etapa-desc">{selectedWorkflow.etapaAtual.descricao}</div>
                    )}
                    <div className="etapa-tipo">Tipo: {selectedWorkflow.etapaAtual.tipo_etapa}</div>
                  </div>
                </div>
              )}

              {selectedWorkflow.execucoes && selectedWorkflow.execucoes.length > 0 && (
                <div className="workflow-detail-section">
                  <h3>Histórico de Execução</h3>
                  <div className="workflow-execucoes-detail">
                    {selectedWorkflow.execucoes.map((exec, idx) => (
                      <div key={idx} className="execucao-detail-item">
                        <div className="execucao-header">
                          <span className="exec-acao">{exec.acao}</span>
                          <span className="exec-data">{formatarData(exec.data_execucao)}</span>
                        </div>
                        <div className="execucao-usuario">
                          Por: {exec.usuarioExecutor?.nome_completo || 'N/A'}
                        </div>
                        {exec.observacoes && (
                          <div className="execucao-observacoes">{exec.observacoes}</div>
                        )}
                        {exec.justificativa && (
                          <div className="execucao-justificativa">
                            <strong>Justificativa:</strong> {exec.justificativa}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {selectedWorkflow.status === 'em_andamento' && selectedWorkflow.etapaAtual && (
                <div className="workflow-detail-section">
                  <h3>Executar Ação</h3>
                  <div className="workflow-acao-form">
                    <div className="form-group">
                      <label>Ação:</label>
                      <select
                        value={acao}
                        onChange={(e) => setAcao(e.target.value)}
                        className="form-select"
                      >
                        <option value="">Selecione uma ação</option>
                        <option value="aprovado">Aprovar</option>
                        {selectedWorkflow.etapaAtual.permite_rejeicao && (
                          <option value="rejeitado">Rejeitar</option>
                        )}
                        <option value="cancelado">Cancelar</option>
                      </select>
                    </div>

                    {acao === 'rejeitado' && (
                      <div className="form-group">
                        <label>Justificativa *:</label>
                        <textarea
                          value={justificativa}
                          onChange={(e) => setJustificativa(e.target.value)}
                          className="form-textarea"
                          rows="3"
                          placeholder="Informe o motivo da rejeição"
                          required
                        />
                      </div>
                    )}

                    <div className="form-group">
                      <label>Observações:</label>
                      <textarea
                        value={observacoes}
                        onChange={(e) => setObservacoes(e.target.value)}
                        className="form-textarea"
                        rows="3"
                        placeholder="Observações adicionais (opcional)"
                      />
                    </div>

                    <div className="form-actions">
                      <button
                        className="btn-executar-acao"
                        onClick={executarAcao}
                        disabled={!acao}
                      >
                        Executar Ação
                      </button>
                      <button
                        className="btn-cancelar-acao"
                        onClick={() => {
                          setAcao('');
                          setObservacoes('');
                          setJustificativa('');
                        }}
                      >
                        Limpar
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Modal: Detalhes do Template */}
      {showModalTemplate && selectedTemplate && (
        <div className="modal-overlay" onClick={() => setShowModalTemplate(false)}>
          <div className="modal-content-workflow" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header-workflow">
              <h2>Detalhes do Template</h2>
              <button className="modal-close" onClick={() => setShowModalTemplate(false)}>×</button>
            </div>

            <div className="modal-body-workflow">
              <div className="workflow-detail-section">
                <h3>Informações do Template</h3>
                <div className="workflow-detail-info">
                  <div className="detail-item">
                    <label>Nome:</label>
                    <span>{selectedTemplate.nome}</span>
                  </div>
                  {selectedTemplate.descricao && (
                    <div className="detail-item">
                      <label>Descrição:</label>
                      <span>{selectedTemplate.descricao}</span>
                    </div>
                  )}
                  <div className="detail-item">
                    <label>Status:</label>
                    <span className={`template-status ${selectedTemplate.ativo ? 'ativo' : 'inativo'}`}>
                      {selectedTemplate.ativo ? '✅ Ativo' : '❌ Inativo'}
                    </span>
                  </div>
                  {selectedTemplate.tipo_documento && (
                    <div className="detail-item">
                      <label>Tipo de Documento:</label>
                      <span>{selectedTemplate.tipo_documento}</span>
                    </div>
                  )}
                </div>
              </div>

              {selectedTemplate.etapas && selectedTemplate.etapas.length > 0 && (
                <div className="workflow-detail-section">
                  <h3>Etapas do Template</h3>
                  <div className="template-etapas-detail">
                    {selectedTemplate.etapas.map((etapa, idx) => (
                      <div key={etapa.id_etapa || idx} className="etapa-detail-card">
                        <div className="etapa-detail-header">
                          <span className="etapa-ordem">{idx + 1}</span>
                          <span className="etapa-nome">{etapa.nome_etapa}</span>
                        </div>
                        {etapa.descricao && (
                          <div className="etapa-desc">{etapa.descricao}</div>
                        )}
                        <div className="etapa-info">
                          <span>Tipo: {etapa.tipo_etapa}</span>
                          {etapa.permite_rejeicao && <span>Permite Rejeição: Sim</span>}
                          {etapa.prazo_dias && <span>Prazo: {etapa.prazo_dias} dias</span>}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Modal: Criar Template */}
      {showModalCriarTemplate && (
        <div className="modal-overlay" onClick={() => setShowModalCriarTemplate(false)}>
          <div className="modal-content-workflow modal-large" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header-workflow">
              <h2>Criar Template de Workflow</h2>
              <button className="modal-close" onClick={() => setShowModalCriarTemplate(false)}>×</button>
            </div>

            <div className="modal-body-workflow">
              <div className="workflow-acao-form">
                <div className="form-group">
                  <label>Nome do Template *:</label>
                  <input
                    type="text"
                    value={novoTemplate.nome}
                    onChange={(e) => setNovoTemplate({ ...novoTemplate, nome: e.target.value })}
                    className="form-select"
                    placeholder="Ex: Aprovação de Memorando"
                  />
                </div>

                <div className="form-group">
                  <label>Descrição:</label>
                  <textarea
                    value={novoTemplate.descricao}
                    onChange={(e) => setNovoTemplate({ ...novoTemplate, descricao: e.target.value })}
                    className="form-textarea"
                    rows="2"
                    placeholder="Descrição do template"
                  />
                </div>

                <div className="form-group">
                  <label>Tipo de Documento:</label>
                  <input
                    type="text"
                    value={novoTemplate.tipo_documento}
                    onChange={(e) => setNovoTemplate({ ...novoTemplate, tipo_documento: e.target.value })}
                    className="form-select"
                    placeholder="Ex: Memorando"
                  />
                </div>

                <div className="form-group">
                  <label>
                    <input
                      type="checkbox"
                      checked={novoTemplate.ativo}
                      onChange={(e) => setNovoTemplate({ ...novoTemplate, ativo: e.target.checked })}
                    />
                    Template Ativo
                  </label>
                </div>

                <div className="workflow-detail-section">
                  <h3>Etapas do Template</h3>
                  
                  {novoTemplate.etapas.length > 0 && (
                    <div className="etapas-criadas">
                      {novoTemplate.etapas.map((etapa, idx) => (
                        <div key={idx} className="etapa-criada-item">
                          <div className="etapa-criada-header">
                            <span>{idx + 1}. {etapa.nome_etapa}</span>
                            <button
                              className="btn-remover-etapa"
                              onClick={() => removerEtapa(idx)}
                            >
                              ✕
                            </button>
                          </div>
                          <div className="etapa-criada-info">
                            Tipo: {etapa.tipo_etapa} | 
                            {etapa.permite_rejeicao ? ' Permite Rejeição' : ' Não permite Rejeição'}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  <div className="adicionar-etapa-form">
                    <h4>Adicionar Etapa</h4>
                    <div className="form-group">
                      <label>Nome da Etapa *:</label>
                      <input
                        type="text"
                        value={etapaAtual.nome_etapa}
                        onChange={(e) => setEtapaAtual({ ...etapaAtual, nome_etapa: e.target.value })}
                        className="form-select"
                        placeholder="Ex: Aprovação do Secretário"
                      />
                    </div>

                    <div className="form-group">
                      <label>Descrição:</label>
                      <textarea
                        value={etapaAtual.descricao}
                        onChange={(e) => setEtapaAtual({ ...etapaAtual, descricao: e.target.value })}
                        className="form-textarea"
                        rows="2"
                        placeholder="Descrição da etapa"
                      />
                    </div>

                    <div className="form-group">
                      <label>Tipo de Etapa:</label>
                      <select
                        value={etapaAtual.tipo_etapa}
                        onChange={(e) => setEtapaAtual({ ...etapaAtual, tipo_etapa: e.target.value })}
                        className="form-select"
                      >
                        <option value="aprovacao">Aprovação</option>
                        <option value="revisao">Revisão</option>
                        <option value="assinatura">Assinatura</option>
                        <option value="publicacao">Publicação</option>
                      </select>
                    </div>

                    <div className="form-group">
                      <label>
                        <input
                          type="checkbox"
                          checked={etapaAtual.permite_rejeicao}
                          onChange={(e) => setEtapaAtual({ ...etapaAtual, permite_rejeicao: e.target.checked })}
                        />
                        Permite Rejeição
                      </label>
                    </div>

                    <button
                      className="btn-adicionar-etapa"
                      onClick={adicionarEtapa}
                    >
                      ➕ Adicionar Etapa
                    </button>
                  </div>
                </div>

                <div className="form-actions">
                  <button
                    className="btn-executar-acao"
                    onClick={criarTemplate}
                    disabled={!novoTemplate.nome.trim() || novoTemplate.etapas.length === 0}
                  >
                    Criar Template
                  </button>
                  <button
                    className="btn-cancelar-acao"
                    onClick={() => setShowModalCriarTemplate(false)}
                  >
                    Cancelar
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Workflow;
